// AppShellHost — pure module-level constants and lookup tables.
//
// Extracted verbatim from AppShellHost.tsx (C1 decomposition). These are
// side-effect-free data and pure helpers the composition root imports back.
// Nothing here reads component state; nothing here performs I/O, randomness,
// or time reads (those helpers stay in the shell).

import type { ComposerResourceRef } from "../../components/Composer/PowerComposer/types.js";
import { resourceDisplayTitle } from "../../components/Navigation/displayTitle.js";
import type {
    ExportRef,
    MaterialRef,
    ResourceRef,
    SourceRef,
} from "../../components/Navigation/types.js";
import type { Library } from "../ports.js";

// The current AppPorts contract still projects one ambient conversation and
// does not expose its domain id. Keep that limitation explicit at the host
// boundary; the plural-tab/port seams can replace this with the selected id.
export const AMBIENT_CONVERSATION_ID = "ambient";

// File extension per domain format id — cosmetic, for the flow's `done` display
// name only. The real filename is server-decided (the manifest title +
// Content-Disposition), so this is a hint, not a contract.
export const extForFormat: Record<string, string> = {
    F_torus: "zip",
    F_canvas: "imscc",
    F_html: "html",
    F_pdf: "pdf",
    F_markdown: "md",
};

// The library's contents as shared-vocabulary refs — the rail lists, id→ref
// resolution and the restored-tab reconciliation (U46) all read this shape.
export const refsOfLibrary = (
    lib: Library,
    untitled: string,
): readonly ResourceRef[] => [
    ...lib.materials.map(
        (m): MaterialRef => ({
            type: "material",
            id: m.id,
            title: resourceDisplayTitle(m.title, "material", untitled),
            ...(m.aiAuthored === undefined ? {} : { aiAuthored: true }),
            ...(m.partOf === undefined ? {} : { partOf: m.partOf }),
        }),
    ),
    ...lib.sources.map(
        (s): SourceRef => ({
            type: "source",
            id: s.id,
            title: resourceDisplayTitle(s.title, "source", untitled),
        }),
    ),
    ...lib.exports.map(
        (e): ExportRef => ({
            type: "export",
            id: e.id,
            title: resourceDisplayTitle(e.title, "export", untitled),
        }),
    ),
];

// Exponential backoff with full jitter for offline read retries (offline round
// §3, lesson 7): plain backoff self-synchronises into a retry storm, so the
// delay is a RANDOM point in [0, cap-capped exponential]. Base 1s, doubling,
// capped at 30s.
export const RETRY_BASE_MS = 1000;
export const RETRY_CAP_MS = 30_000;
// While online, ping the server this often so a mid-session disconnect is
// noticed promptly — `navigator.onLine` lies (it stays true when the SERVER
// dies but the network is up), so the truth is a real request (offline round
// §1 lesson 5).
export const HEARTBEAT_MS = 10_000;
// A ResourceRef, restated as the composer's own vocabulary for the `@`-mention
// menu (PowerComposer/types.js). Pure rename across the one kind where the two
// sums disagree ("export" here vs. "package" there — the composer's is the
// author-facing noun; ResourceRef's is the domain's).
export const toComposerResourceRef = (
    ref: ResourceRef,
): ComposerResourceRef => ({
    id: ref.id,
    title: ref.title,
    kind: ref.type === "export" ? "package" : ref.type,
    ...(ref.type === "material" && ref.aiAuthored !== undefined
        ? { aiAuthored: true }
        : {}),
});

// The landing's capability-tip rotation cadence — slow and calm so it teaches
// without competing with the composer for attention.
export const TIP_ROTATION_MS = 9000;
